// Client/client-vite/src/Calls/callDealer.ts
import { DEALER_BASE } from "@/config";
import axios, { AxiosError } from "axios";

/* --------------------------------------------
   Helper utilities
-------------------------------------------- */
function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === "object" && v !== null;
}

function pickErrorMessage(err: unknown): string {
    if (axios.isAxiosError(err)) {
        const ax = err as AxiosError<unknown>;
        const data = ax.response?.data;
        if (typeof data === "string") return data;
        if (isRecord(data)) {
            if (typeof data.message === "string") return data.message;
            if (typeof data.error === "string") return data.error;
        }
        return ax.message || "Request failed";
    }
    return err instanceof Error ? err.message : "Request failed";
}


/* --------------------------------------------
   Axios setup for dealer routes
-------------------------------------------- */
export const dealerHttp = axios.create({
    baseURL: DEALER_BASE, // e.g. https://api.idscrm.com/api/dealers
    withCredentials: true, // 🚀 include dealer cookie in every call
    headers: {
        "Content-Type": "application/json",
    },
});


/* --------------------------------------------
   API Functions
-------------------------------------------- */

/*-------------------------------------------- 
   Types
-------------------------------------------- */
export interface Dealer {
    id: number;
    company: string;
    email: string;
    username: string;
}

export interface BdcPersonInput {
    bdc_firstname: string;
    bdc_lastname: string;
    bdc_email: string;
    bdc_password: string;
    bdc_mobilephone_number?: string;
    bdc_job_title?: string;
}

export interface BdcPersonResponse {
    ok: boolean;
    message: string;
    bdc_person?: {
        bdc_id: number;
        bdc_firstname: string;
        bdc_lastname: string;
        bdc_email: string;
        main_dealerid: number;
        created_at: string;
    };
}

export interface SalespersonInput {
    salesperson_firstname: string;
    salesperson_lastname: string;
    salesperson_email: string;
    salesperson_username?: string;
    salesperson_password: string;
    salesperson_mobilephone_number?: string;
    salesperson_job_title?: string;
}

export interface SalespersonResponse {
    ok: boolean;
    message: string;
    salesperson?: {
        salesperson_id: number;
        salesperson_firstname: string;
        salesperson_lastname: string;
        salesperson_email: string;
        salesperson_username: string;
        main_dealerid: number;
        created_at: string;
    };
}


export interface FinanceManagerInput {
    financemanager_firstname: string;
    financemanager_lastname: string;
    financemanager_email: string;
    financemanager_password: string;
    financemanager_mobilephone_number?: string;
    financemanager_job_title?: string;
}

export interface FinanceManagerResponse {
    ok: boolean;
    message: string;
    financemanager?: {
        financemanager_id: number;
        financemanager_firstname: string;
        financemanager_lastname: string;
        financemanager_email: string;
        main_dealerid: number;
        created_at: string;
    };
}


export interface ServiceManagerInput {
    servicemanager_firstname: string;
    servicemanager_lastname: string;
    servicemanager_email: string;
    servicemanager_password: string;
    servicemanager_mobilephone_number?: string;
    servicemanager_job_title?: string;
}

export interface ServiceManagerResponse {
    ok: boolean;
    message: string;
    servicemanager?: {
        servicemanager_id: number;
        servicemanager_firstname: string;
        servicemanager_lastname: string;
        servicemanager_email: string;
        main_dealerid: number;
        created_at: string;
    };
}


/** 🔹 List all dealers (public-safe) */
export async function listDealers(): Promise<Dealer[]> {
    try {
        const res = await dealerHttp.get<Dealer[]>("/");
        return res.data;
    } catch (e) {
        console.error("listDealers error:", e);
        throw new Error(pickErrorMessage(e));
    }
}


/** 🔹 Get dealer by ID */
export async function getDealerById(dealerId: number): Promise<Dealer | null> {
    try {
        const res = await dealerHttp.get<Dealer>(`/id/${dealerId}`);
        return res.data;
    } catch (e) {
        console.error("getDealerById error:", e);
        throw new Error(pickErrorMessage(e));
    }
}

/** 🔹 Create a new salesperson (requires dealer cookie auth) */
export async function createSalesperson(data: SalespersonInput): Promise<SalespersonResponse> {
    try {
        const res = await dealerHttp.post<SalespersonResponse>("/sales-person-create", data);
        return res.data;
    } catch (e) {
        console.error("createSalesperson error:", e);
        throw new Error(pickErrorMessage(e));
    }
}

/* --------------------------------------------
   BDC Person Creation
-------------------------------------------- */

/** 🔹 Create a new BDC rep (requires dealer cookie auth) */
export async function createBdcPerson(
    data: BdcPersonInput
): Promise<BdcPersonResponse> {
    try {
        const res = await dealerHttp.post<BdcPersonResponse>(
            "/bdc-person-create",
            data
        );
        return res.data;
    } catch (e) {
        console.error("createBdcPerson error:", e);
        throw new Error(pickErrorMessage(e));
    }
}


/* --------------------------------------------
   Finance Manager Creation
-------------------------------------------- */


export async function createFinanceManager(
    data: FinanceManagerInput
): Promise<FinanceManagerResponse> {
    try {
        const res = await dealerHttp.post<FinanceManagerResponse>(
            "/finance-manager-create",
            data
        );
        return res.data;
    } catch (e) {
        console.error("createFinanceManager error:", e);
        throw new Error(pickErrorMessage(e));
    }
}



/* --------------------------------------------
   Service Manager Creation
-------------------------------------------- */

export async function createServiceManager(
    data: ServiceManagerInput
): Promise<ServiceManagerResponse> {
    try {
        const res = await dealerHttp.post<ServiceManagerResponse>(
            "/service-manager-create",
            data
        );
        return res.data;
    } catch (e) {
        console.error("createServiceManager error:", e);
        throw new Error(pickErrorMessage(e));
    }
}

